import { mergeClasses } from '@blank/utilities'
import React, {
  forwardRef,
  MouseEvent,
  PropsWithChildren,
  useMemo,
} from 'react'
import { Link } from '../../Link/Link'
import { Spinner } from '../Spinner/Spinner'
import { useButtonTheme } from './useButtonTheme'
import { useSpinnerTheme } from './useSpinnerTheme'

export const BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'tertiary',
  'icon',
  'none',
] as const
export const BUTTON_COLORS = [
  'none',
  'default',
  'warning',
  'danger',
  'highlighted',
] as const
export const BUTTON_SIZE = ['large', 'small']

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number]
export type ButtonColor = (typeof BUTTON_COLORS)[number]
export type ButtonSize = (typeof BUTTON_SIZE)[number]

export interface ButtonDesignOptionProps {
  variant: ButtonVariant
  color?: ButtonColor
  size?: ButtonSize
}

export type ButtonBaseProps = {
  isLoading?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  buttonContentClassName?: string
} & (
  | (React.ComponentProps<'button'> & {
      href?: never
      target?: never
      type?: string
      rel?: never
    })
  | (React.ComponentProps<'a'> & {
      href: string
      target?: string
      type?: never
      rel?: string
    })
) & {
    onClick?: (
      event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>
    ) => void
  }

export type ButtonProps = ButtonBaseProps & ButtonDesignOptionProps

export const Button = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>(
  (
    {
      color,
      children,
      disabled,
      href,
      icon,
      iconPosition = 'left',
      isLoading,
      variant,
      size = 'large',
      buttonContentClassName,
      ...buttonProps
    }: ButtonProps,
    ref
  ) => {
    const { buttonClasses } = useButtonTheme({
      color,
      variant,
      disabled,
      isLoading,
      size,
    })

    const { isSpinnerOnTop, spinnerClasses } = useSpinnerTheme({
      color,
      variant,
    })

    const iconContent = useMemo(() => {
      if (!isSpinnerOnTop && isLoading) {
        return <Spinner {...spinnerClasses} />
      }
      return (
        <>
          {isLoading && <Spinner {...spinnerClasses} />}
          {icon}
        </>
      )
    }, [icon, isLoading, isSpinnerOnTop, spinnerClasses])

    const AsElement = href && !disabled ? Link : 'button'

    return (
      <AsElement
        disabled={disabled}
        {...(!disabled ? { href } : null)}
        // Button and Anchor props keep clashing, any was the only workaround I found
        {...(buttonProps as any)}
        className={mergeClasses(buttonClasses, buttonProps.className)}
        ref={ref}
      >
        <span
          className={mergeClasses(
            'flex flex-row items-center justify-center gap-2',
            { 'bg-transparent text-transparent': isLoading && isSpinnerOnTop },
            buttonContentClassName
          )}
        >
          {iconPosition === 'left' && iconContent}
          {variant !== 'icon' && children}
          {iconPosition === 'right' && iconContent}
        </span>
      </AsElement>
    )
  }
)
